import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import type { AccessState, AdminMembership, AdminSession, AdminUser } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { Trail } from "@/ui/Trail";
import { ReasonDialog } from "@/features/admin/ReasonDialog";
import { useToast } from "@/ui/Toast";
import { Chip } from "@/ui/Chip";
import { Button } from "@/ui/Button";
import { Dialog } from "@/ui/Overlay";
import { Field, Input } from "@/ui/Field";
import { ACCESS_LABEL, ACCESS_TONE } from "@/features/admin/packages";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, Skeleton } from "@/ui/States";
import { absoluteTime, relativeTime, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/users/$userId")({
  component: PlatformPersonScreen,
});

/**
 * One person, for somebody helping them.
 *
 * No credential appears here and none can: the API returns facts about the
 * account — whether the address is confirmed, whether a second factor is on,
 * which organisations, which sessions — and never a way into it. The two
 * actions are the two a support conversation actually reaches for.
 */
function PlatformPersonScreen() {
  const { userId } = Route.useParams();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Which reason is being asked for, or null. One piece of state for the three
  // actions here, because exactly one of them can be open at a time.
  const [asking, setAsking] = useState<"revoke" | "suspend" | "restore" | null>(null);

  const detail = useQuery({
    queryKey: ["platform-user", userId],
    queryFn: ({ signal }) =>
      api.get<{
        user: AdminUser;
        memberships: AdminMembership[] | null;
        sessions: AdminSession[] | null;
      }>(`/admin/users/${userId}`, undefined, signal),
  });


  if (detail.isLoading) return <Skeleton className="h-64 w-full rounded-xl" />;
  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;

  const user = detail.data!.user;
  const memberships = detail.data?.memberships ?? [];
  const sessions = detail.data?.sessions ?? [];

  return (
    <div className="flex flex-col gap-6">
      <Head
        above={
          <Trail
            trail={[{ label: "People", to: "/admin/users" }, { label: user.email }]}
          />
        }
        title={user.email}
        description={user.display_name || "No display name."}
        actions={
          <div className="flex gap-2">
            <Button
              tone="secondary"
              size="dense"
              disabled={sessions.length === 0}
              onClick={() => setAsking("revoke")}
            >
              Sign out everywhere
            </Button>
            {/* Suspending is refused for staff accounts by the API, so the
                button is not offered either: an action whose only outcome is a
                refusal is an action that should not be on the screen. */}
            {!user.platform_admin &&
              (user.status === "suspended" ? (
                <Button tone="secondary" size="dense" onClick={() => setAsking("restore")}>
                  Lift suspension
                </Button>
              ) : (
                <Button tone="secondary" size="dense" onClick={() => setAsking("suspend")}>
                  Suspend
                </Button>
              ))}
            <Button tone="danger" size="dense" onClick={() => setConfirmDelete(true)}>
              Delete account
            </Button>
          </div>
        }
      />

      {/* Said at the top, with the reason. A suspension that is only a badge
          halfway down a list of badges is a suspension the next person misses,
          and then wonders why the customer cannot sign in. */}
      {user.status === "suspended" && (
        <div className="rounded-xl border border-[var(--danger)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--danger)]">
            This account is suspended and cannot sign in
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            {user.suspension_reason || "No reason was recorded."}
            {user.suspended_at && ` · ${relativeTime(user.suspended_at)}`}
          </p>
        </div>
      )}

      <AccessPanel user={user} onDecided={() => detail.refetch()} />

      <Panel>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Address" value={user.email_verified ? "Confirmed" : "Not confirmed"} />
          <Fact label="Two-factor" value={user.mfa_enabled ? "On" : "Off"} />
          <Fact label="Organisations" value={String(user.organisations)} />
          <Fact
            label="Joined"
            value={relativeTime(user.created_at)}
            title={absoluteTime(user.created_at)}
          />
        </div>
        {user.platform_admin && (
          <p className="mt-4 text-[13px] text-[var(--text-secondary)]">
            <Chip tone="live">Staff</Chip>{" "}
            This account can read and change every tenant. It cannot be deleted here. Platform
            administration is granted and revoked from the command line, against the control
            plane&apos;s own database.
          </p>
        )}
      </Panel>

      <Panel title="Organisations" description="Where this person is a member, and as what." padded={false}>
        {memberships.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="No organisations" description="This account belongs to none." />
          </div>
        ) : (
          <Table dense fixed>
            <thead>
              <Tr>
                <Th width="w-[56%]">Organisation</Th>
                <Th width="w-[24%]">Role</Th>
                <Th width="w-[20%]" align="right">Joined</Th>
              </Tr>
            </thead>
            <tbody>
              {memberships.map((m) => (
                <Tr key={m.organisation_id}>
                  <Td>
                    <Link
                      to="/admin/$orgId"
                      params={{ orgId: m.organisation_id }}
                      className="font-medium text-[var(--text-primary)] hover:underline"
                    >
                      {m.organisation_name}
                    </Link>
                  </Td>
                  <Td>{m.role}</Td>
                  <Td align="right" title={absoluteTime(m.joined_at)}>
                    {shortTime(m.joined_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      <Panel
        title="Live sessions"
        description="Where this account is signed in. Ending them needs no password and is reversible by signing in again."
        padded={false}
      >
        {sessions.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="Not signed in anywhere" description="There is no live session." />
          </div>
        ) : (
          <Table dense fixed>
            <thead>
              <Tr>
                <Th width="w-[40%]">Device</Th>
                <Th width="w-[20%]">Address</Th>
                <Th width="w-[20%]" align="right">Last seen</Th>
                <Th width="w-[20%]" align="right">Expires</Th>
              </Tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <Tr key={s.id}>
                  <Td title={s.user_agent || undefined}>{s.user_agent || "Unknown"}</Td>
                  <Td className="mono text-[13px]">{s.ip_address || "—"}</Td>
                  <Td
                    align="right"
                    title={s.last_seen_at ? absoluteTime(s.last_seen_at) : undefined}
                  >
                    {s.last_seen_at ? shortTime(s.last_seen_at) : "—"}
                  </Td>
                  <Td align="right" title={absoluteTime(s.expires_at)}>
                    {shortTime(s.expires_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      {asking === "revoke" && (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setAsking(null)}
          title="Sign this person out everywhere"
          description="Every live session ends. They can sign in again straight away, which is what makes this the safe first move when somebody thinks another person is in their account."
          confirmLabel="Sign them out"
          action={(reason) =>
            api.post<{ revoked: number }>(`/admin/users/${userId}/sessions/revoke`, { reason })
          }
          onDone={async () => {
            setAsking(null);
            await detail.refetch();
          }}
        />
      )}

      {asking === "suspend" && (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setAsking(null)}
          title={`Suspend ${user.email}`}
          description="They cannot sign in, and their live sessions end immediately. The organisations they belong to keep running — stopping a tenant is a separate decision."
          confirmLabel="Suspend"
          danger
          action={(reason) => api.post<void>(`/admin/users/${userId}/suspend`, { reason })}
          onDone={async () => {
            setAsking(null);
            await detail.refetch();
          }}
        />
      )}

      {asking === "restore" && (
        <ReasonDialog
          open
          onOpenChange={(open) => !open && setAsking(null)}
          title={`Lift the suspension on ${user.email}`}
          description="They can sign in again. The failed-attempt lockout is cleared too, so they do not meet a second wall made of the attempts they made while suspended."
          confirmLabel="Lift it"
          action={(reason) => api.post<void>(`/admin/users/${userId}/restore`, { reason })}
          onDone={async () => {
            setAsking(null);
            await detail.refetch();
          }}
        />
      )}

      {confirmDelete && (
        <DeleteAccountDialog
          userId={userId}
          email={user.email}
          onClose={() => setConfirmDelete(false)}
          onDeleted={() => void navigate({ to: "/admin/users", replace: true })}
        />
      )}
    </div>
  );
}

function Fact({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
        {label}
      </p>
      <p className="mt-1 text-sm text-[var(--text-primary)]" title={title}>
        {value}
      </p>
    </div>
  );
}

/**
 * Deleting is typed out, not clicked.
 *
 * The row goes and everything referencing it goes with it. A confirmation that
 * is one click away from a list is a confirmation somebody clicks by accident.
 */
function DeleteAccountDialog({
  userId,
  email,
  onClose,
  onDeleted,
}: {
  userId: string;
  email: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const toast = useToast();

  const remove = useMutation({
    mutationFn: () =>
      api.delete<void>(`/admin/users/${userId}`, { reason: reason.trim() || "Removed by staff" }),
    onSuccess: () => {
      toast.success("Account deleted");
      onDeleted();
    },
    onError: toast.error,
  });

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Delete this account"
      description="The row is removed, not flagged. Everything referencing it goes with it, and this cannot be undone."
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            tone="danger"
            busy={remove.isPending}
            disabled={typed !== email || reason.trim().length < 3}
            onClick={() => remove.mutate()}
          >
            Delete
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Type the address to confirm" hint={email}>
          {({ id }) => (
            <Input
              id={id}
              value={typed}
              autoFocus
              className="mono text-[13px]"
              onChange={(e) => setTyped(e.target.value)}
            />
          )}
        </Field>
        <Field label="Reason" hint="Recorded in the audit log.">
          {({ id }) => <Input id={id} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * Whether this person may use the platform.
 *
 * The decision and the reason are one action, because the reason is not a note
 * to ourselves: it goes into the email either way. Somebody who registered and
 * heard nothing registers again, and then writes to support, so the message is
 * sent whichever way the decision goes and the box is labelled for the person
 * who will read it.
 */
function AccessPanel({ user, onDecided }: { user: AdminUser; onDecided: () => void }) {
  const [note, setNote] = useState("");
  const toast = useToast();

  const decide = useMutation({
    mutationFn: (access: AccessState) =>
      api.post<void>(`/admin/users/${user.id}/access`, { access, note: note.trim() }),
    onSuccess: (_, access) => {
      toast.success(
        access === "approved" ? "Approved, and told" : "Turned down, and told",
        "The account has been emailed.",
      );
      setNote("");
      onDecided();
    },
    onError: toast.error,
  });

  const waiting = user.access === "pending";

  return (
    <Panel
      title="Beta access"
      description={
        waiting
          ? "This account registered and is waiting. Until it is approved it can sign in and see nothing else."
          : "Already decided. Changing it takes effect immediately and the account is emailed again."
      }
    >
      <div className="flex flex-wrap items-center gap-3">
        <Chip tone={ACCESS_TONE[user.access] ?? undefined}>
          {ACCESS_LABEL[user.access] ?? user.access}
        </Chip>
        {user.access_note && (
          <span className="text-[13px] text-[var(--text-secondary)]">“{user.access_note}”</span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Field
            label="What to tell them"
            hint="Goes into the email. Write it for the person reading it."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                value={note}
                placeholder={waiting ? "Welcome. Tell us what you build." : "Optional"}
                aria-describedby={describedBy}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Button
          tone="primary"
          size="dense"
          busy={decide.isPending && decide.variables === "approved"}
          disabled={user.access === "approved"}
          onClick={() => decide.mutate("approved")}
        >
          Approve
        </Button>
        <Button
          tone="secondary"
          size="dense"
          busy={decide.isPending && decide.variables === "rejected"}
          disabled={user.access === "rejected"}
          onClick={() => decide.mutate("rejected")}
        >
          Turn down
        </Button>
      </div>
    </Panel>
  );
}
